import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, RefreshCw, LogOut, ShoppingBag, DollarSign, Clock, CheckCircle2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["awaiting_payment", "confirmed", "preparing", "ready", "fulfilled", "cancelled"];
const PAYMENT_STATUS_OPTIONS = ["pending", "pending_confirmation", "paid", "refunded", "failed"];

const Admin = () => {
  const { user, loading, login, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [filter, setFilter] = useState({ status: "", payment_status: "" });
  const [busyOrder, setBusyOrder] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!user?.is_admin) return;
    setFetching(true);
    setError("");
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.payment_status) params.payment_status = filter.payment_status;
      const [o, s] = await Promise.all([
        axios.get(`${API}/admin/orders`, { params, withCredentials: true }),
        axios.get(`${API}/admin/orders/stats`, { withCredentials: true }),
      ]);
      setOrders(o.data.orders || []);
      setStats(s.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setFetching(false);
    }
  }, [user, filter]);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  const updateOrder = async (id, body) => {
    setBusyOrder(id);
    try {
      const res = await axios.patch(`${API}/admin/orders/${id}`, body, { withCredentials: true });
      setOrders((prev) => prev.map((o) => (o.id === id ? res.data.order : o)));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusyOrder(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--awd-bg)" }}>
        <Loader2 size={32} className="animate-spin" style={{ color: "var(--awd-ochre)" }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--awd-bg)" }}>
        <div className="max-w-md w-full text-center p-10 border" style={{ borderColor: "var(--awd-border)" }} data-testid="admin-login">
          <div className="kente-stripe mb-8" />
          <Lock size={36} className="mx-auto" style={{ color: "var(--awd-ochre)" }} />
          <h1 className="font-display text-4xl mt-6">Admin Access</h1>
          <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>Sign in with your authorized Google account to view orders.</p>
          <button onClick={login} className="btn-primary mt-8 w-full" data-testid="admin-login-button">Sign in with Google</button>
          <button onClick={() => navigate("/")} className="mt-4 text-xs uppercase tracking-widest link-reveal" style={{ color: "var(--awd-text-3)" }}>← Back to site</button>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--awd-bg)" }}>
        <div className="max-w-md text-center p-10 border" style={{ borderColor: "var(--awd-jollof)" }} data-testid="admin-denied">
          <h1 className="font-display text-3xl">Access denied</h1>
          <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>
            <strong>{user.email}</strong> is not on the admin list.
          </p>
          <button onClick={logout} className="btn-ghost mt-6">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--awd-bg)" }} data-testid="admin-dashboard">
      <div className="kente-stripe" />
      <header className="border-b sticky top-0 z-30" style={{ background: "rgba(15,13,12,0.92)", backdropFilter: "blur(12px)", borderColor: "var(--awd-border)" }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <div>
            <div className="eyebrow">Admin</div>
            <h1 className="font-display text-2xl">Afro World Delight · Orders</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm" style={{ color: "var(--awd-text-2)" }}>{user.email}</span>
            <button onClick={load} className="p-2 border" style={{ borderColor: "var(--awd-border-2)" }} title="Refresh" data-testid="admin-refresh">
              <RefreshCw size={16} className={fetching ? "animate-spin" : ""} />
            </button>
            <button onClick={logout} className="p-2 border" style={{ borderColor: "var(--awd-border-2)" }} title="Sign out" data-testid="admin-logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8 space-y-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={<ShoppingBag size={18} />} label="Total Orders" value={stats.total_orders} />
            <StatCard icon={<CheckCircle2 size={18} />} label="Paid" value={stats.paid_orders} />
            <StatCard icon={<Clock size={18} />} label="Pending" value={stats.pending_orders} />
            <StatCard icon={<DollarSign size={18} />} label="Revenue" value={`$${stats.revenue.toFixed(2)}`} />
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4">
          <FilterSelect label="Order Status" value={filter.status} onChange={(v) => setFilter({ ...filter, status: v })} options={STATUS_OPTIONS} />
          <FilterSelect label="Payment Status" value={filter.payment_status} onChange={(v) => setFilter({ ...filter, payment_status: v })} options={PAYMENT_STATUS_OPTIONS} />
        </div>

        {error && <div className="p-3 text-sm" style={{ background: "rgba(155,34,38,0.15)", color: "#ffb4b4", border: "1px solid var(--awd-jollof)" }}>{error}</div>}

        {/* Orders */}
        <div className="space-y-4" data-testid="admin-orders-list">
          {orders.length === 0 && !fetching && (
            <div className="text-center py-16" style={{ color: "var(--awd-text-3)" }}>No orders yet.</div>
          )}
          {orders.map((o) => (
            <OrderRow key={o.id} order={o} busy={busyOrder === o.id} onUpdate={(b) => updateOrder(o.id, b)} />
          ))}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value }) => (
  <div className="p-5 border" style={{ borderColor: "var(--awd-border)", background: "var(--awd-surface)" }}>
    <div className="flex items-center gap-2 label-mini" style={{ color: "var(--awd-ochre)" }}>{icon}{label}</div>
    <div className="font-display text-3xl mt-1">{value}</div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div className="flex flex-col">
    <span className="label-mini mb-1">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="field" style={{ background: "var(--awd-surface)", padding: "0.6rem 0.75rem", border: "1px solid var(--awd-border-2)" }} data-testid={`filter-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <option value="">All</option>
      {options.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
    </select>
  </div>
);

const OrderRow = ({ order, busy, onUpdate }) => {
  const date = new Date(order.created_at);
  const pmBadge = order.payment_method?.startsWith("stripe") ? "STRIPE" : order.payment_method?.toUpperCase();
  const paymentColor = order.payment_status === "paid" ? "var(--awd-ochre)" :
                      order.payment_status === "pending_confirmation" ? "#f59e0b" :
                      order.payment_status === "failed" ? "var(--awd-jollof)" : "var(--awd-text-3)";
  return (
    <div className="p-5 border grid grid-cols-1 md:grid-cols-12 gap-4" style={{ borderColor: "var(--awd-border)", background: "var(--awd-surface)" }} data-testid={`admin-order-${order.id}`}>
      <div className="md:col-span-3">
        <div className="label-mini">Ref · {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
        <div className="font-display text-lg mt-1">{order.id.slice(0, 8)}</div>
        <div className="text-xs mt-1" style={{ color: paymentColor }}>● {order.payment_status?.replace(/_/g, " ")} · {pmBadge}</div>
      </div>
      <div className="md:col-span-3">
        <div className="label-mini">Customer</div>
        <div className="text-sm mt-1">{order.customer_name}</div>
        <div className="text-xs" style={{ color: "var(--awd-text-2)" }}>{order.customer_phone}</div>
        {order.customer_email && <div className="text-xs" style={{ color: "var(--awd-text-3)" }}>{order.customer_email}</div>}
        {order.notes && <div className="text-xs mt-2 italic" style={{ color: "var(--awd-text-3)" }}>"{order.notes}"</div>}
      </div>
      <div className="md:col-span-3">
        <div className="label-mini">Items</div>
        <div className="text-sm mt-1">
          {order.items?.map((it) => (
            <div key={it.id}>{it.quantity}× {it.name}</div>
          ))}
        </div>
        <div className="font-display text-xl mt-2" style={{ color: "var(--awd-ochre)" }}>${order.amount?.toFixed(2)}</div>
      </div>
      <div className="md:col-span-3 flex flex-col gap-2">
        <div className="label-mini">Update</div>
        <select className="field" style={{ padding: "0.45rem 0.6rem", border: "1px solid var(--awd-border-2)" }} value={order.status || ""} onChange={(e) => onUpdate({ status: e.target.value })} disabled={busy} data-testid={`order-status-${order.id}`}>
          {STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
        <select className="field" style={{ padding: "0.45rem 0.6rem", border: "1px solid var(--awd-border-2)" }} value={order.payment_status || ""} onChange={(e) => onUpdate({ payment_status: e.target.value })} disabled={busy} data-testid={`order-payment-${order.id}`}>
          {PAYMENT_STATUS_OPTIONS.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
        </select>
      </div>
    </div>
  );
};

export default Admin;
